import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import { useNavigate } from 'react-router-dom';

const CompanyTab = () => {
  const { organization, profile, refreshOrganization } = useAuth();
  const navigate = useNavigate();
  const [companyData, setCompanyData] = useState({
    name: '',
    website: '',
    industry: '',
    size: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US',
    timezone: 'America/Los_Angeles',
    currency: 'USD',
    dateFormat: 'MM/DD/YYYY'
  });
  const [isLoading, setIsLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (organization) {
      const settings = organization?.settings || {};
      setCompanyData({
        name: organization?.name || '',
        website: settings?.website || '',
        industry: settings?.industry || '',
        size: settings?.size || '',
        phone: settings?.phone || '',
        address: settings?.address || '',
        city: settings?.city || '',
        state: settings?.state || '',
        zipCode: settings?.zipCode || '',
        country: settings?.country || settings?.country || 'US',
        timezone: settings?.timezone || 'America/Los_Angeles',
        currency: settings?.currency || 'USD',
        dateFormat: settings?.dateFormat || 'MM/DD/YYYY'
      });
    }
  }, [organization]);

  const industryOptions = [
    { value: 'Plumbing', label: '🔧 Plumbing' },
    { value: 'HVAC', label: '❄️ HVAC (Heating & Cooling)' },
    { value: 'Electrical', label: '⚡ Electrical' },
    { value: 'Cleaning', label: '🧹 Cleaning Services' },
    { value: 'Landscaping', label: '🌿 Landscaping & Lawn Care' },
    { value: 'Pest Control', label: '🐛 Pest Control' },
    { value: 'Roofing', label: '🏠 Roofing' },
    { value: 'Painting', label: '🖌️ Painting' },
    { value: 'Pool Service', label: '🏊 Pool Service' },
    { value: 'Moving', label: '🚚 Moving & Relocation' },
    { value: 'General Contractor', label: '🔨 General Contractor' },
    { value: 'Handyman', label: '🛠️ Handyman Services' },
    { value: 'Appliance Repair', label: '🔌 Appliance Repair' },
    { value: 'Flooring', label: '🪵 Flooring' },
    { value: 'Garage Door', label: '🚪 Garage Door Services' },
    { value: 'Locksmith', label: '🔑 Locksmith' },
    { value: 'Security Systems', label: '🔒 Security Systems' },
    { value: 'Solar', label: '☀️ Solar Installation' },
    { value: 'Tree Service', label: '🌳 Tree Service' },
    { value: 'Junk Removal', label: '🗑️ Junk Removal' },
    { value: 'Pressure Washing', label: '💧 Pressure Washing' },
    { value: 'Window Cleaning', label: '🪟 Window Cleaning' },
    { value: 'Other', label: '⚙️ Other Service' },
  ];

  const sizeOptions = [
    { value: '1-10', label: '1-10 employees' },
    { value: '11-50', label: '11-50 employees' },
    { value: '50-200', label: '50-200 employees' },
    { value: '200-1000', label: '200-1000 employees' },
    { value: '1000+', label: '1000+ employees' }
  ];

  const currencyOptions = [
    { value: 'USD', label: 'US Dollar ($)' },
    { value: 'EUR', label: 'Euro (€)' },
    { value: 'GBP', label: 'British Pound (£)' },
    { value: 'CAD', label: 'Canadian Dollar (C$)' }
  ];

  const handleCompanyChange = (field, value) => {
    setCompanyData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveCompany = async () => {
    if (!organization?.id) {
      setErrorMsg('Organization not found. Please refresh the page.');
      return;
    }
    setIsLoading(true);
    setSuccessMsg('');
    setErrorMsg('');
    try {
      const { name, ...settingsFields } = companyData;

      // Merge new settings with existing settings to preserve other fields (sms, etc.)
      const existingSettings = organization?.settings || {};
      const updatedSettings = {
        ...existingSettings,
        website: settingsFields?.website,
        industry: settingsFields?.industry,
        size: settingsFields?.size,
        phone: settingsFields?.phone,
        address: settingsFields?.address,
        city: settingsFields?.city,
        state: settingsFields?.state,
        zipCode: settingsFields?.zipCode,
        country: settingsFields?.country,
        timezone: settingsFields?.timezone,
        currency: settingsFields?.currency,
        dateFormat: settingsFields?.dateFormat,
      };

      const { data, error } = await supabase?.from('organizations')?.update({
          name: name?.trim() || organization?.name,
          settings: updatedSettings,
          updated_at: new Date()?.toISOString(),
        })?.eq('id', organization?.id)?.select()?.single();

      if (error) {
        console.error('Company save error:', error);
        throw new Error(error?.message || 'Failed to save. Check your permissions.');
      }

      // Refresh organization in context
      await refreshOrganization();

      setSuccessMsg('Company settings saved successfully!');
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      console.error('handleSaveCompany error:', err);
      setErrorMsg(err?.message || 'Failed to save company settings. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const businessTypeLabel = organization?.business_type
    ? organization?.business_type?.replace(/_/g, ' ')?.replace(/\b\w/g, (c) => c?.toUpperCase())
    : null;

  const handleChangeBusinessType = async () => {
    if (window.confirm('Changing your business type will reset your pipeline stages, templates, and call scripts. Are you sure?')) {
      await supabase?.from('organizations')?.update({ onboarding_completed: false })?.eq('id', organization?.id);
      await refreshOrganization();
      navigate('/onboarding');
    }
  };

  return (
    <div className="space-y-8">
      {successMsg && <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">{successMsg}</div>}
      {errorMsg && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{errorMsg}</div>}

      {/* Business Type Banner */}
      {businessTypeLabel && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Briefcase" size={16} className="text-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">Business Type: {businessTypeLabel}</p>
              <p className="text-xs text-muted-foreground">Your CRM is personalized for {businessTypeLabel}</p>
            </div>
          </div>
          <button
            onClick={handleChangeBusinessType}
            className="text-xs text-primary hover:underline font-medium"
          >
            Change Business Type
          </button>
        </div>
      )}

      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center space-x-4 mb-6">
          <Icon name="Building2" size={24} className="text-primary" />
          <div>
            <h3 className="text-lg font-semibold text-card-foreground">Company Information</h3>
            <p className="text-sm text-muted-foreground">Update your organization details and branding</p>
          </div>
        </div>
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Company Name" type="text" value={companyData?.name}
              onChange={(e) => handleCompanyChange('name', e?.target?.value)} required />
            <Input label="Website" type="url" value={companyData?.website}
              onChange={(e) => handleCompanyChange('website', e?.target?.value)} placeholder="https://www.example.com" />
            <Select label="Industry" options={industryOptions} value={companyData?.industry}
              onChange={(value) => handleCompanyChange('industry', value)} searchable />
            <Select label="Company Size" options={sizeOptions} value={companyData?.size}
              onChange={(value) => handleCompanyChange('size', value)} />
            <Input label="Phone Number" type="tel" value={companyData?.phone}
              onChange={(e) => handleCompanyChange('phone', e?.target?.value)} />
          </div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center space-x-4 mb-6">
          <Icon name="MapPin" size={24} className="text-primary" />
          <div>
            <h3 className="text-lg font-semibold text-card-foreground">Business Address</h3>
            <p className="text-sm text-muted-foreground">Your company's primary business location</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Input label="Street Address" type="text" value={companyData?.address}
              onChange={(e) => handleCompanyChange('address', e?.target?.value)} />
          </div>
          <Input label="City" type="text" value={companyData?.city}
            onChange={(e) => handleCompanyChange('city', e?.target?.value)} />
          <Input label="State/Province" type="text" value={companyData?.state}
            onChange={(e) => handleCompanyChange('state', e?.target?.value)} />
          <Input label="ZIP/Postal Code" type="text" value={companyData?.zipCode}
            onChange={(e) => handleCompanyChange('zipCode', e?.target?.value)} />
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center space-x-4 mb-6">
          <Icon name="Globe" size={24} className="text-primary" />
          <div>
            <h3 className="text-lg font-semibold text-card-foreground">Regional Settings</h3>
            <p className="text-sm text-muted-foreground">Configure timezone and currency</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select label="Currency" options={currencyOptions} value={companyData?.currency}
            onChange={(value) => handleCompanyChange('currency', value)} />
        </div>
      </div>

      <div className="flex justify-end">
        <Button variant="default" onClick={handleSaveCompany} loading={isLoading} iconName="Save" iconPosition="left">
          Save Company Settings
        </Button>
      </div>
    </div>
  );
};

export default CompanyTab;